import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import {
  getVarianceAnalysis,
  type VarianceAnalysisRow,
} from '@/services/costingService';
import { ActualCostDetailsModal } from './components/ActualCostDetailsModal';
import { formatINR } from '@/utils/format';
import { useWarehouseStore } from '@/stores/warehouseStore';

type Tab = 'variance' | 'profitability';

// Helper function to format variance with colored arrow indicator matching user mockup
function formatVariancePill(val: number) {
  // If val < 0 (actual > std), unfavorable cost overrun => Red with up arrow
  // If val > 0 (actual < std), favorable cost savings => Green with down arrow
  if (val < 0) {
    return (
      <span className="text-rose-600 font-semibold inline-flex items-center gap-0.5">
        <span>↑</span> {formatINR(Math.abs(val))}
      </span>
    );
  }
  if (val > 0) {
    return (
      <span className="text-emerald-600 font-semibold inline-flex items-center gap-0.5">
        <span>↓</span> {formatINR(val)}
      </span>
    );
  }
  return <span className="text-slate-500 font-semibold">₹0</span>;
}

export default function VariancePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedWarehouseId } = useWarehouseStore();

  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(
    tabParam === 'profitability' ? 'profitability' : 'variance'
  );

  const [loading, setLoading] = useState(true);
  const [varianceRows, setVarianceRows] = useState<VarianceAnalysisRow[]>([]);
  const [selectedRowForModal, setSelectedRowForModal] = useState<VarianceAnalysisRow | null>(null);
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');

  // Sync tab with URL query parameter
  useEffect(() => {
    if (tabParam === 'profitability') {
      setActiveTab('profitability');
    } else if (tabParam === 'variance') {
      setActiveTab('variance');
    }
  }, [tabParam]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'profitability' ? { tab: 'profitability' } : {});
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getVarianceAnalysis(selectedWarehouseId);
      if (res && res.success && Array.isArray(res.data)) {
        setVarianceRows(res.data.filter((r) => (Number(r.stdTotal) > 0 || Number(r.actTotal) > 0)));
      } else {
        setVarianceRows([]);
      }
    } catch (err) {
      console.error('Failed to load variance analysis:', err);
      setVarianceRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedWarehouseId]);

  // Compute dynamic KPI metrics across all variance rows matching screenshot
  const summary = useMemo(() => {
    const totalStdMat = varianceRows.reduce((s, r) => s + (Number(r.stdMaterial) || 0), 0);
    const totalActMat = varianceRows.reduce((s, r) => s + (Number(r.actMaterial) || 0), 0);
    const totalStdLab = varianceRows.reduce((s, r) => s + (Number(r.stdLabour) || 0), 0);
    const totalActLab = varianceRows.reduce((s, r) => s + (Number(r.actLabour) || 0), 0);
    const totalStdOH = varianceRows.reduce((s, r) => s + (Number(r.stdOverhead) || 0), 0);
    const totalActOH = varianceRows.reduce((s, r) => s + (Number(r.actOverhead) || 0), 0);
    const totalStdTot = varianceRows.reduce((s, r) => s + (Number(r.stdTotal) || 0), 0);
    const totalActTot = varianceRows.reduce((s, r) => s + (Number(r.actTotal) || 0), 0);

    const matVar = totalStdMat - totalActMat;
    const labVar = totalStdLab - totalActLab;
    const ohVar = totalStdOH - totalActOH;
    const totalVar = totalStdTot - totalActTot;
    const varPct = totalStdTot > 0 ? ((totalVar / totalStdTot) * 100).toFixed(1) : '0.0';

    return {
      totalStdMat,
      totalActMat,
      matVar,
      totalStdLab,
      totalActLab,
      labVar,
      totalStdOH,
      totalActOH,
      ohVar,
      totalStdTot,
      totalActTot,
      totalVar,
      varPct,
    };
  }, [varianceRows]);

  // Profitability per costed order
  const profitabilityItems = useMemo(() => {
    const items = varianceRows.map((r) => {
      // Sale price is the item's sale rate saved with the cost record
      const salePrice = Number(r.salePrice) || 0;
      const costPerUnit = r.qty > 0 ? r.actTotal / r.qty : 0;
      const margin = salePrice - costPerUnit;
      const marginPct = salePrice > 0 ? (margin / salePrice) * 100 : 0;
      const totalRevenue = salePrice * r.qty;
      const totalCost = r.actTotal;
      const totalProfit = totalRevenue - totalCost;

      let gradeLetter = 'A';
      let gradeLabel = 'Excellent';
      let gradeBadgeClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';

      if (marginPct < 20) {
        gradeLetter = 'C';
        gradeLabel = 'Average';
        gradeBadgeClass = 'bg-amber-50 text-amber-700 border-amber-200';
      } else if (marginPct < 50) {
        gradeLetter = 'B';
        gradeLabel = 'Good';
        gradeBadgeClass = 'bg-blue-50 text-blue-700 border-blue-200';
      }

      return {
        ...r,
        salePrice,
        costPerUnit,
        margin,
        marginPct,
        totalRevenue,
        totalCost,
        totalProfit,
        gradeLetter,
        gradeLabel,
        gradeBadgeClass,
      };
    });

    items.sort((a, b) =>
      sortDirection === 'desc' ? b.marginPct - a.marginPct : a.marginPct - b.marginPct
    );

    return items;
  }, [varianceRows, sortDirection]);

  const profitStats = useMemo(() => {
    if (profitabilityItems.length === 0) {
      return {
        best: { name: '—', pct: 0 },
        worst: { name: '—', pct: 0 },
        avgMarginPct: 0,
        totalRevenue: 0,
        totalCost: 0,
        totalProfit: 0,
      };
    }

    const sortedByPct = [...profitabilityItems].sort((a, b) => b.marginPct - a.marginPct);
    const bestItem = sortedByPct[0];
    const worstItem = sortedByPct[sortedByPct.length - 1];

    const totalRev = profitabilityItems.reduce((acc, i) => acc + i.totalRevenue, 0);
    const totalCst = profitabilityItems.reduce((acc, i) => acc + i.totalCost, 0);
    const totalProf = totalRev - totalCst;
    // Average margin percent across displayed products
    const sumMarginPct = profitabilityItems.reduce((acc, i) => acc + i.marginPct, 0);
    const avgPct = profitabilityItems.length > 0 ? sumMarginPct / profitabilityItems.length : 0;

    return {
      best: { name: bestItem.productName, pct: bestItem.marginPct },
      worst: { name: worstItem.productName, pct: worstItem.marginPct },
      avgMarginPct: avgPct,
      totalRevenue: totalRev,
      totalCost: totalCst,
      totalProfit: totalProf,
    };
  }, [profitabilityItems]);

  return (
    <AppLayout>
      <div className="space-y-6 p-4 md:p-6 max-w-[1700px] mx-auto min-h-screen bg-[#f8fafc]/40">

        {/* ── Top Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Cost Variance Analysis</h1>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">Standard vs Actual cost comparison</p>
          </div>
          <button
            onClick={() => navigate('/manufacturing/costing')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 shadow-2xs transition-colors cursor-pointer self-start sm:self-auto"
          >
            <i className="ri-arrow-left-line text-sm" /> Back to Dashboard
          </button>
        </div>

        {/* ── Tab Navigation ── */}
        <div className="flex border-b border-slate-200 gap-6">
          <button
            onClick={() => handleTabChange('variance')}
            className={`pb-3 text-xs font-bold transition-all border-b-2 cursor-pointer ${
              activeTab === 'variance'
                ? 'border-indigo-600 text-slate-900'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            Variance Analysis
          </button>
          <button
            onClick={() => handleTabChange('profitability')}
            className={`pb-3 text-xs font-bold transition-all border-b-2 cursor-pointer ${
              activeTab === 'profitability'
                ? 'border-slate-900 text-slate-900 font-extrabold'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            Profitability
          </button>
        </div>

        {/* ── 4 KPI Summary Cards (visible only on variance tab as in screenshot) ── */}
        {activeTab === 'variance' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Material Variance */}
            <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs space-y-2">
              <div className="text-xs text-slate-400 font-medium">Material Variance</div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-slate-900">
                  {formatINR(Math.abs(summary.matVar))}
                </span>
                <span className={`text-xs font-semibold ${summary.matVar < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {summary.matVar < 0 ? '↑' : '↓'} {formatINR(Math.abs(summary.matVar))}
                </span>
              </div>
              <div className="text-[11px] text-slate-400 font-medium">
                Std: {formatINR(summary.totalStdMat)} | Act: {formatINR(summary.totalActMat)}
              </div>
            </div>

            {/* Card 2: Labour Variance */}
            <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs space-y-2">
              <div className="text-xs text-slate-400 font-medium">Labour Variance</div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-slate-900">
                  {formatINR(Math.abs(summary.labVar))}
                </span>
                <span className={`text-xs font-semibold ${summary.labVar < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {summary.labVar < 0 ? '↑' : '↓'} {formatINR(Math.abs(summary.labVar))}
                </span>
              </div>
              <div className="text-[11px] text-slate-400 font-medium">
                Std: {formatINR(summary.totalStdLab)} | Act: {formatINR(summary.totalActLab)}
              </div>
            </div>

            {/* Card 3: Overhead Variance */}
            <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs space-y-2">
              <div className="text-xs text-slate-400 font-medium">Overhead Variance</div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-slate-900">
                  {formatINR(Math.abs(summary.ohVar))}
                </span>
                <span className={`text-xs font-semibold ${summary.ohVar < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {summary.ohVar < 0 ? '↑' : '↓'} {formatINR(Math.abs(summary.ohVar))}
                </span>
              </div>
              <div className="text-[11px] text-slate-400 font-medium">
                Std: {formatINR(summary.totalStdOH)} | Act: {formatINR(summary.totalActOH)}
              </div>
            </div>

            {/* Card 4: Total Cost Variance */}
            <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs space-y-2">
              <div className="text-xs text-slate-400 font-medium">Total Cost Variance</div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-slate-900">
                  {formatINR(Math.abs(summary.totalVar))}
                </span>
                <span className={`text-xs font-semibold ${summary.totalVar < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {summary.totalVar < 0 ? '↑' : '↓'} {formatINR(Math.abs(summary.totalVar))}
                </span>
              </div>
              <div className="text-[11px] text-slate-400 font-medium">
                Variance %: {Number(summary.varPct) > 0 ? `+${summary.varPct}%` : `${summary.varPct}%`}
              </div>
            </div>
          </div>
        )}

        {/* ── TAB 1: VARIANCE ANALYSIS TABLE ── */}
        {activeTab === 'variance' && (
          <div className="bg-white border border-slate-200/90 rounded-xl shadow-2xs overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Standard vs Actual Comparison
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200 text-slate-500 font-semibold select-none text-[11px]">
                    <th className="py-3 px-3.5 whitespace-nowrap">PO Number</th>
                    <th className="py-3 px-3.5 whitespace-nowrap">Product</th>
                    <th className="py-3 px-3 text-center whitespace-nowrap">Qty</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap">Std Material</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap">Act Material</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap">Mat Var</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap">Std Labour</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap">Act Labour</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap">Lab Var</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap">Std OH</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap">Act OH</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap">OH Var</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap font-bold text-slate-700">Std Total</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap font-bold text-slate-700">Act Total</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap font-bold text-slate-700">Total Var</th>
                    <th className="py-3 px-3 text-right whitespace-nowrap font-bold text-slate-700">Var %</th>
                    <th className="py-3 px-3.5 text-center whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {varianceRows.map((row) => (
                    <tr
                      key={row.poNumber}
                      className="hover:bg-slate-50/60 transition-colors"
                    >
                      {/* PO Number */}
                      <td className="py-3 px-3.5 font-bold text-slate-900 whitespace-nowrap">
                        {row.poNumber}
                      </td>

                      {/* Product */}
                      <td className="py-3 px-3.5 whitespace-nowrap">
                        <div className="font-bold text-slate-900">{row.productName}</div>
                        {row.variantName && (
                          <div className="text-[11px] text-slate-400 mt-0.5 font-normal">
                            {row.variantName}
                          </div>
                        )}
                      </td>

                      {/* Qty */}
                      <td className="py-3 px-3 text-center text-slate-700 whitespace-nowrap">
                        {row.qty} {row.unit}
                      </td>

                      {/* Material Columns */}
                      <td className="py-3 px-3 text-right text-slate-700 whitespace-nowrap">
                        {formatINR(row.stdMaterial)}
                      </td>
                      <td className="py-3 px-3 text-right text-slate-700 whitespace-nowrap">
                        {formatINR(row.actMaterial)}
                      </td>
                      <td className="py-3 px-3 text-right whitespace-nowrap">
                        {formatVariancePill(row.matVar)}
                      </td>

                      {/* Labour Columns */}
                      <td className="py-3 px-3 text-right text-slate-700 whitespace-nowrap">
                        {formatINR(row.stdLabour)}
                      </td>
                      <td className="py-3 px-3 text-right text-slate-700 whitespace-nowrap">
                        {formatINR(row.actLabour)}
                      </td>
                      <td className="py-3 px-3 text-right whitespace-nowrap">
                        {formatVariancePill(row.labVar)}
                      </td>

                      {/* Overhead Columns */}
                      <td className="py-3 px-3 text-right text-slate-700 whitespace-nowrap">
                        {formatINR(row.stdOverhead)}
                      </td>
                      <td className="py-3 px-3 text-right text-slate-700 whitespace-nowrap">
                        {formatINR(row.actOverhead)}
                      </td>
                      <td className="py-3 px-3 text-right whitespace-nowrap">
                        {formatVariancePill(row.ohVar)}
                      </td>

                      {/* Totals & Percent */}
                      <td className="py-3 px-3 text-right font-bold text-slate-900 whitespace-nowrap">
                        {formatINR(row.stdTotal)}
                      </td>
                      <td className="py-3 px-3 text-right font-bold text-slate-900 whitespace-nowrap">
                        {formatINR(row.actTotal)}
                      </td>
                      <td className="py-3 px-3 text-right whitespace-nowrap font-bold">
                        {formatVariancePill(row.totalVar)}
                      </td>
                      <td className={`py-3 px-3 text-right whitespace-nowrap font-bold ${
                        row.varPct < 0 ? 'text-rose-600' : 'text-emerald-600'
                      }`}>
                        {row.varPct.toFixed(1)}%
                      </td>

                      {/* Action View */}
                      <td className="py-3 px-3.5 text-center whitespace-nowrap">
                        <button
                          onClick={() => setSelectedRowForModal(row)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer shadow-2xs transition-colors"
                          title="View Actual Cost Details"
                        >
                          <i className="ri-eye-line text-xs" /> View
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!loading && varianceRows.length === 0 && (
                    <tr>
                      <td colSpan={17} className="py-10 text-center text-slate-400">
                        No variance records available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB 2: PROFITABILITY REPORT (EXACT MATCH OF media_1791437031927.png) ── */}
        {activeTab === 'profitability' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200/90 rounded-xl shadow-2xs overflow-hidden">
              {/* Header with Sort button */}
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Profitability Report
                </h3>
                <button
                  onClick={() => setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer shadow-2xs transition-colors"
                >
                  <span>{sortDirection === 'desc' ? '↓' : '↑'} Margin %</span>
                </button>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-200 text-slate-500 font-semibold select-none text-[11px]">
                      <th className="py-3 px-5 whitespace-nowrap">Product</th>
                      <th className="py-3 px-4 whitespace-nowrap">Variant</th>
                      <th className="py-3 px-4 whitespace-nowrap">PO No</th>
                      <th className="py-3 px-3 text-left whitespace-nowrap">Qty</th>
                      <th className="py-3 px-4 text-left whitespace-nowrap">Cost/Unit</th>
                      <th className="py-3 px-4 text-left whitespace-nowrap">Sale Price</th>
                      <th className="py-3 px-4 text-left whitespace-nowrap">Margin</th>
                      <th className="py-3 px-4 text-left whitespace-nowrap">Margin %</th>
                      <th className="py-3 px-5 text-left whitespace-nowrap">Grade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium">
                    {profitabilityItems.map((item) => (
                      <tr key={item.poNumber} className="hover:bg-slate-50/60 transition-colors">
                        <td className="py-3.5 px-5 font-bold text-slate-900 whitespace-nowrap">
                          {item.productName}
                        </td>
                        <td className="py-3.5 px-4 text-slate-500 whitespace-nowrap">
                          {item.variantName || '—'}
                        </td>
                        <td className="py-3.5 px-4 text-slate-600 whitespace-nowrap">
                          {item.poNumber}
                        </td>
                        <td className="py-3.5 px-3 text-slate-600 whitespace-nowrap">
                          {item.qty} {item.unit}
                        </td>
                        <td className="py-3.5 px-4 text-slate-700 whitespace-nowrap">
                          {item.costPerUnit % 1 === 0 ? formatINR(item.costPerUnit) : `₹${item.costPerUnit.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`}
                        </td>
                        <td className="py-3.5 px-4 text-slate-700 whitespace-nowrap">
                          {formatINR(item.salePrice)}
                        </td>
                        <td className="py-3.5 px-4 text-slate-700 whitespace-nowrap">
                          {item.margin % 1 === 0 ? formatINR(item.margin) : `₹${item.margin.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`}
                        </td>
                        <td className={`py-3.5 px-4 font-bold whitespace-nowrap ${
                          item.marginPct >= 50
                            ? 'text-emerald-600'
                            : item.marginPct >= 20
                            ? 'text-blue-600'
                            : 'text-amber-600'
                        }`}>
                          {item.marginPct.toFixed(1)}%
                        </td>
                        <td className="py-3.5 px-5 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] rounded-full border ${item.gradeBadgeClass}`}>
                            <span className="font-black">{item.gradeLetter}</span>
                            <span className="font-medium">{item.gradeLabel}</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Profitability Summary Metrics Bar directly below table (Exact match of screenshot) */}
              <div className="px-5 py-4 bg-slate-50/50 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-xs">
                <div>
                  <span className="text-slate-400 block text-[11px] font-medium mb-0.5">Best Margin</span>
                  <span className="text-slate-900 font-bold">{profitStats.best.name} at {profitStats.best.pct.toFixed(1)}%</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px] font-medium mb-0.5">Worst Margin</span>
                  <span className="text-slate-900 font-bold">{profitStats.worst.name} at {profitStats.worst.pct.toFixed(1)}%</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px] font-medium mb-0.5">Average Margin</span>
                  <span className="text-slate-900 font-bold">{profitStats.avgMarginPct.toFixed(1)}%</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px] font-medium mb-0.5">Total Revenue</span>
                  <span className="text-slate-900 font-bold">{formatINR(profitStats.totalRevenue)}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px] font-medium mb-0.5">Total Cost</span>
                  <span className="text-slate-900 font-bold">{formatINR(profitStats.totalCost)}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px] font-medium mb-0.5">Total Profit</span>
                  <span className="text-emerald-600 font-bold">{formatINR(profitStats.totalProfit)}</span>
                </div>
              </div>
            </div>

            {/* Cost vs Sale Price Trend (Horizontal stacked bars matching media_1791437031927.png) */}
            <div className="bg-white border border-slate-200/90 rounded-xl p-5 shadow-2xs space-y-4">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Cost vs Sale Price Trend
              </h3>

              <div className="space-y-4 pt-1">
                {profitabilityItems.map((item) => {
                  const costPct = item.salePrice > 0 ? (item.costPerUnit / item.salePrice) * 100 : 50;
                  const marginPct = 100 - costPct;
                  const label = item.variantName || item.productName;

                  return (
                    <div key={item.poNumber} className="flex items-center gap-4 text-xs font-medium">
                      {/* Product / Variant label on the left */}
                      <span className="w-48 text-slate-700 font-semibold shrink-0 truncate">
                        {label}
                      </span>

                      {/* Full-width Stacked Horizontal Bar in the center */}
                      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
                        <div
                          style={{ width: `${Math.min(100, Math.max(0, costPct))}%` }}
                          className="bg-[#2563eb] h-full transition-all duration-500"
                          title={`Cost: ${costPct.toFixed(1)}%`}
                        />
                        <div
                          style={{ width: `${Math.min(100, Math.max(0, marginPct))}%` }}
                          className="bg-[#10b981] h-full transition-all duration-500"
                          title={`Margin: ${marginPct.toFixed(1)}%`}
                        />
                      </div>

                      {/* Numbers on the right: Sale Price & Margin % */}
                      <div className="flex items-center gap-4 shrink-0 w-32 justify-end">
                        <span className="text-slate-700 font-medium">{formatINR(item.salePrice)}</span>
                        <span className="text-slate-900 font-bold">{item.marginPct.toFixed(1)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend matching screenshot */}
              <div className="flex items-center gap-4 pt-2 text-xs text-slate-600 font-medium">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-xs bg-[#2563eb]" />
                  <span>Cost</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-xs bg-[#10b981]" />
                  <span>Margin</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Actual Cost Details Modal (Exact replica of media_1791437074079.png) ── */}
        {selectedRowForModal && (
          <ActualCostDetailsModal
            open={!!selectedRowForModal}
            onClose={() => setSelectedRowForModal(null)}
            row={selectedRowForModal}
          />
        )}

      </div>
    </AppLayout>
  );
}